<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-white border-b border-gray-100 px-6 py-4">
            <div class="max-w-3xl mx-auto">
                <BasePageTitle no-border title="IMPACT Leader Code Self-Reflection" subtitle="Take a few minutes to reflect on your leadership behaviors." />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <Transition name="fade" mode="out-in">
                <div v-if="phase === 'instructions'" key="instructions" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-valence-purple/10 flex items-center justify-center shrink-0">
                                <i class="bi bi-list-check text-valence-purple text-lg" aria-hidden="true"></i>
                            </div>
                            <!-- FIXME: Consider extracting assessment section headers to a shared BasePageHeader component -->
                            <h2 class="title-body-bold text-gray-900">How This Works</h2>
                        </div>
                        <p class="body text-gray-700 mb-4">
                            This self-reflection is grounded on the IMPACT Leader Code – that reflects our 3 commitments and 10 behaviors to support our Mission-Based culture
                            reset.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            We invite you to consider how consistently the behavior shows up in your day-to-day leadership using a rating scale from 1 (not yet) to 5 (always).
                            These reflections are for your own development. Once you complete the self-reflection you will be able to start coaching with Nadia to take it further.
                        </p>
                        <p class="body text-gray-700">Let's take a look at an example first.</p>
                    </div>
                </div>

                <div v-else-if="phase === 'example'" key="example" class="max-w-3xl mx-auto px-6 py-10 space-y-5">
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-valence-purple/10 flex items-center justify-center shrink-0">
                                <i class="bi bi-eye text-valence-purple text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-gray-900">Example Question</h2>
                        </div>
                        <p class="body-small text-gray-500 mb-6">
                            You will be presented a series of behaviors on which you will be asked to reflect. Here is what a question looks like:
                        </p>
                        <div class="bg-gray-50 rounded-xl border border-gray-100 p-5 mb-6">
                            <div class="flex items-start justify-between gap-4 mb-3">
                                <div class="flex-1">
                                    <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                                    <p class="details font-bold tracking-widest text-valence-purple uppercase mb-1">Invent the Future</p>
                                    <h3 class="body-bold text-gray-900">I invent the future and create innovative, differentiated, and competitive solutions for our customers.</h3>
                                </div>
                                <div class="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-gray-200 text-gray-400">
                                    <span class="title-body-bold leading-none">—</span>
                                </div>
                            </div>
                            <div class="mt-3">
                                <div class="h-1 rounded-full bg-gray-200 w-full mb-2"></div>
                                <div class="flex justify-between px-[10px]">
                                    <span v-for="n in 5" :key="n" class="text-[10px] text-gray-300 font-semibold">{{ n }} · {{ rating_labels[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="body-small-bold text-gray-700 mb-3">Rating scale (applies to all questions):</p>
                        <div class="divide-y divide-gray-50">
                            <div v-for="n in 5" :key="n" class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-valence-purple/10 text-valence-purple details flex items-center justify-center">
                                    {{ n }}
                                </div>
                                <div>
                                    <span class="body-small-bold text-gray-900">{{ rating_labels[String(n)] }}</span>
                                    <span class="body-small text-gray-400"> — {{ rating_descriptions[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <div class="shrink-0 bg-white border-t border-gray-100 px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <button
                    v-if="phase !== 'instructions'"
                    class="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 body-bold hover:bg-gray-50 transition-colors"
                    :disabled="isStartingAssessment"
                    @click="emit('intro-prev')">
                    ← Back
                </button>
                <div v-else></div>
                <button
                    class="px-6 py-2.5 rounded-xl body-bold bg-valence-purple text-white hover:bg-valence-purple/90 transition-colors"
                    :disabled="isStartingAssessment"
                    @click="emit('intro-next')">
                    {{ phase === "example" ? (isStartingAssessment ? "Starting..." : "Start Self-Reflection →") : "Continue →" }}
                </button>
            </div>
            <div v-if="startError" class="max-w-3xl mx-auto mt-2 details text-amber-600">
                {{ startError }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BasePageTitle from "~vue/components/BasePageTitle.vue";

type IntroPhase = "instructions" | "example";

interface Props {
    phase: IntroPhase;
    isStartingAssessment: boolean;
    startError: string | null;
    rating_labels: Record<string, string>;
    rating_descriptions: Record<string, string>;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "intro-prev"): void;
    (e: "intro-next"): void;
}>();
</script>
